/**
 * Import Monitor — Date Inconsistency Exception and New PO Date Inconsistency
 * Exception tabs (one component, `kind` picks the list).
 *
 * CR-9031. The two lists the old Supplier Portal Data Import page carried:
 * open PO lines whose ODD, Customer Required Date and Supplier Promise Date
 * disagree, either after the customer accepted a supplier proposal or while
 * the line is still New. Follows the Monitor's Customer selection.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  dateInconsistencyAPI,
  type DateInconsistencyKind,
  type DateInconsistencyResponse,
} from '../../lib/api';
import { CUSTOMER_REQUIRED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../config/dateLabels';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import PoLineLink from '../../components/common/PoLineLink';
import { Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

interface ImportDateInconsistencyTabProps {
  customerId: number;
  kind: DateInconsistencyKind;
}

const PAGE_SIZE = 25;

const COPY: Record<DateInconsistencyKind, { title: string; file: string; conditions: string[] }> = {
  accepted: {
    title: 'Date Inconsistency Exception',
    file: 'date_exception',
    conditions: [
      'Still Open (not completed).',
      `${ORIGINAL_REQUIRED_DATE}, ${CUSTOMER_REQUIRED_DATE} and ${SUPPLIER_PROMISE_DATE} are not the same.`,
      'The customer has accepted the proposed change dates from the supplier.',
    ],
  },
  new: {
    title: 'New PO Date Inconsistency Exception',
    file: 'new_po_date_exception',
    conditions: [
      'The PO Line is in "New" status.',
      `There is a discrepancy between any of the following dates on the PO Line: ${ORIGINAL_REQUIRED_DATE}, ${CUSTOMER_REQUIRED_DATE}, ${SUPPLIER_PROMISE_DATE}.`,
    ],
  },
};

export default function ImportDateInconsistencyTab({ customerId, kind }: ImportDateInconsistencyTabProps) {
  const { formatDate } = useFormatters();
  const [data, setData] = useState<DateInconsistencyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const copy = COPY[kind];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await dateInconsistencyAPI.list(customerId, kind, search || undefined, page, PAGE_SIZE));
    } catch (err: unknown) {
      console.error('Date exception load failed:', err);
      setError('Failed to load date exceptions');
      toast.error('Failed to load date exceptions');
    } finally {
      setLoading(false);
    }
  }, [customerId, kind, search, page]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await dateInconsistencyAPI.exportXlsx(customerId, kind, search || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${copy.file}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err: unknown) {
      console.error('Date exception export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const date = (v: string | null) => (v ? formatDate(v) : '—');

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold">
          {copy.title}
          {data && <span className="ml-2 text-muted-foreground font-normal">({data.count.toLocaleString()})</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          This report shows all PO lines that meet ALL of the following conditions:
          <ol className="list-decimal ml-5 mt-1">
            {copy.conditions.map(c => <li key={c}>{c}</li>)}
          </ol>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO, PO/line, part or supplier…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); }
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} aria-label="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || !data || data.count === 0} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export to Excel
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || data.results.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No PO lines meet these conditions for this customer.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-muted-foreground">PO Number</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">Line</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">Supplier</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">{ORIGINAL_REQUIRED_DATE}</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">{CUSTOMER_REQUIRED_DATE}</th>
                    <th className="px-4 py-3 font-semibold text-muted-foreground">{SUPPLIER_PROMISE_DATE}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((row) => (
                    <tr key={row.po_line_id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{row.po_number}</td>
                      <td className="px-4 py-3">
                        <PoLineLink poLineId={row.po_line_id}>{row.line_number}</PoLineLink>
                      </td>
                      <td className="px-4 py-3">{row.supplier_name || '—'}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{date(row.original_required_date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{date(row.customer_required_date)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{date(row.supplier_promise_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} lines
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
